/**
 * Devoción cart drawer redesign — interactive behaviors.
 *
 * Augments the existing cart-drawer-component / cart-items-component shell.
 * Owns:
 *   - upsell rail arrow scroll + add-to-cart cards
 *   - climate-offset toggle (adds/removes the offset line item)
 *   - subscription cadence <select> + convert-to-subscription button
 *   - user remove buttons (line, spoon, gift)
 *   - qty-1 trash + qty stepper loading states
 *   - loading / skeleton / announce / toast / checkout-loading helpers
 *   - gift reconcilers: the first-subscription measuring spoon (evaluateSpoon),
 *     gift-with-purchase (evaluateGift), and its discount code (applyAutoCodes)
 *
 * Cart mutations go through Shopify's AJAX cart endpoints, then dispatch
 * CartUpdateEvent so cart-items-component morphs the section in place. The three
 * gift reconcilers each issue at most one mutation per pass and then converge
 * (after adding, the gift is present so it won't re-add; #attemptedCodes only
 * grows), so they cannot feed an update loop. Every other mutation is a single
 * response to a discrete user action. The climate-offset guardrail
 * (evaluateClimateGuardrail) removes the offset line when no real product
 * remains, so the offset can never be the only thing in the cart; it issues one
 * mutation then converges (the line count is 0 afterwards).
 */
import { CartUpdateEvent } from '@theme/events';

const ROUTES = (typeof Theme !== 'undefined' && Theme.routes) || null;
const CART_CHANGE_URL = ROUTES?.cart_change_url || '/cart/change.js';
const CART_ADD_URL = ROUTES?.cart_add_url || '/cart/add.js';
const CART_UPDATE_URL = ROUTES?.cart_update_url || '/cart/update.js';
// Base for applying a discount code to the cart session. Shopify's AJAX cart API
// has no discount endpoint, so we GET /discount/{code} — its side effect applies
// the code; we re-render after. Used to zero a GWP gift line so it shows free.
// root_url is the locale prefix (e.g. "/" or "/en") — present at runtime but not
// in the typed Routes shape, so read it through a cast.
const ROOT_URL = (ROUTES && /** @type {any} */ (ROUTES).root_url) || '/';
const DISCOUNT_BASE = `${String(ROOT_URL).replace(/\/$/, '')}/discount/`;

const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };

/**
 * Returns the active section id (the header section that owns the cart drawer).
 * @param {HTMLElement} root - The cart drawer root element.
 * @returns {string | null} The section id, or null if it can't be resolved.
 */
function getSectionId(root) {
  const cartItems = root.closest('cart-items-component') || root.querySelector('cart-items-component');
  if (!(cartItems instanceof HTMLElement)) return null;
  return cartItems.dataset.sectionId ?? null;
}

/**
 * Calls a Shopify cart endpoint with section morphing enabled.
 * @param {string} url - The cart endpoint URL.
 * @param {Record<string, any>} body - The request body (id/quantity/selling_plan/etc).
 * @param {string | null} sectionId - The section id to morph, if any.
 * @returns {Promise<any>} The parsed JSON response (cart object or error).
 */
async function cartFetch(url, body, sectionId) {
  /** @type {Record<string, any>} */
  const payload = { ...body };
  if (sectionId) payload.sections = sectionId;
  const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
  return res.json();
}

/**
 * Dispatches a CartUpdateEvent so cart-items-component morphs.
 *
 * Queues via microtask so the dispatch fires AFTER the caller's finally{}
 * clears its own this.busy flag, keeping the mutation atomic.
 * @param {any} data - The updated cart object (with sections).
 * @param {string | null} sectionId - The section id to morph.
 * @param {string} [source] - Source tag carried on the event.
 * @returns {void}
 */
function dispatchCartUpdate(data, sectionId, source = 'awp-cart-drawer') {
  if (!sectionId) return;
  queueMicrotask(() => {
    document.dispatchEvent(
      new CartUpdateEvent(data, sectionId, {
        itemCount: data?.item_count ?? 0,
        source,
        sections: data?.sections,
      })
    );
  });
}

/**
 * True when a cart endpoint response is an error rather than a cart object. A
 * successful response is the cart and has no `status`; an error sets `status` to
 * a number (e.g. 404, 422) OR a string token (e.g. "bad_request").
 * @param {any} data - The parsed cart response.
 * @returns {boolean} True when the response represents an error.
 */
function isCartError(data) {
  if (!data) return true;
  if (typeof data.status === 'number') return data.status >= 400;
  if (typeof data.status === 'string') return data.status !== 'ok';
  return false;
}

/**
 * True when a cart-change error means the targeted line is already gone — a
 * stale line key — rather than a genuine failure.
 * @param {any} data - The parsed cart error response.
 * @returns {boolean} True when the error indicates a missing/stale line.
 */
function isStaleLineError(data) {
  if (!data) return false;
  const msg = String(data.message || data.description || '').toLowerCase();
  return (
    msg.includes('no valid id') ||
    msg.includes("doesn't exist") ||
    msg.includes('cannot find') ||
    data.status === 404
  );
}

/**
 * Shows a transient error toast at body level.
 * @param {HTMLElement} root - The drawer root (unused beyond scoping intent).
 * @param {string | undefined} message - The message to show.
 * @returns {void}
 */
function showError(root, message) {
  let toast = document.querySelector('.awp_cart-toast');
  if (!(toast instanceof HTMLElement)) {
    toast = document.createElement('div');
    toast.className = 'awp_cart-toast';
    document.body.appendChild(toast);
  }
  toast.textContent = message || 'Something went wrong. Please try again.';
  toast.classList.add('is-visible');
  setTimeout(() => toast.classList.remove('is-visible'), 3000);
}

/**
 * Announces a message to assistive tech via a persistent visually-hidden polite
 * live region. Lives at body level so the announcement survives the section
 * morph that follows a cart mutation.
 * @param {string} message - The message to announce.
 * @returns {void}
 */
function announce(message) {
  if (!message) return;
  let region = document.querySelector('.awp_cart-live');
  if (!(region instanceof HTMLElement)) {
    region = document.createElement('div');
    region.className = 'awp_cart-live visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    document.body.appendChild(region);
  }
  // Clear immediately, then set after a short delay so it lands AFTER the
  // section morph settles and so an identical message re-announces.
  region.textContent = '';
  const target = region;
  setTimeout(() => {
    target.textContent = message;
  }, 200);
}

/**
 * Reads a string message off a thrown value for the error toast.
 * @param {unknown} err - The caught error.
 * @returns {string} A human-readable message.
 */
function errorMessage(err) {
  return err instanceof Error ? err.message : String(err);
}

/**
 * AWP cart drawer behavior controller. One instance per drawer root.
 *
 * morph.copyAttributes strips any attribute not in the new HTML, so storing
 * "wired" flags on DOM datasets is unreliable — they vanish after each
 * cart:update morph. We track wired elements in a JS-side WeakSet so the
 * bookkeeping is invisible to morph, then call rebind() on each cart:update to
 * wire any new elements that appeared (e.g. the climate row when the cart goes
 * from empty → not).
 */
class AwpCartDrawerController {
  /** @type {WeakSet<Element>} */
  #wired = new WeakSet();

  /** @type {HTMLElement} */
  root;

  /** @type {string | null} */
  sectionId;

  /** @type {boolean} */
  busy = false;

  /** GWP discount codes already attempted this session (upper-cased), so an
   *  invalid/non-applicable code can't re-apply on every morph. The set only
   *  grows, so applyAutoCodes() strictly converges to a no-op.
   *  @type {Set<string>} */
  #attemptedCodes = new Set();

  /**
   * @param {HTMLElement} root - The `.awp_cart-drawer__root` element.
   */
  constructor(root) {
    this.root = root;
    this.sectionId = getSectionId(root);

    // dialog:open is non-bubbling and dispatched on the cart-drawer-component
    // host, so we listen on the host element directly (see below).
    const host = root.closest('cart-drawer-component');

    // A cart mutation re-renders (morphs) the drawer's section, and
    // morph.copyAttributes strips any attribute absent from the server HTML —
    // including data-awp-drawer-open, the RUNTIME flag dialog.js sets on the host
    // to show the manual backdrop and enable click-outside-to-close. If the drawer
    // is OPEN during that morph the flag is silently stripped: the overlay
    // vanishes (opacity/visibility) and its pointer-events revert to none, so
    // clicks fall through and outside-click stops closing the drawer. This bites
    // the /cart → /collections redirect auto-open, where the drawer opens and the
    // spoon/gift reconcilers then POST to /cart. Re-assert the flag (and the
    // header z-index lift, likewise class-based) after every morph while the
    // dialog is genuinely open (and not mid-close), before the next paint.
    const preserveDrawerOpenState = () => {
      const dialog = host?.querySelector('.cart-drawer__dialog');
      if (
        dialog instanceof HTMLDialogElement &&
        dialog.open &&
        !dialog.classList.contains('dialog-closing')
      ) {
        host?.setAttribute('data-awp-drawer-open', '');
        host?.closest('.header-section')?.classList.add('awp_cart-header-lifted');
      }
    };

    // Defer rebind to a microtask so it runs AFTER the morph: cart updates
    // dispatch cart:update first, then morph the section. By the next microtask
    // the DOM is fresh and our wiring reads up-to-date nodes.
    //
    // rebind() is pure DOM wiring (no network) and is safe on every pass. The
    // gift/spoon/discount reconcilers are split out into reconcile() because they
    // can POST to the cart (/cart/change.js, /cart/update.js). We only reconcile
    // in response to a REAL cart mutation (cart:update) or when the drawer is
    // actually opened (dialog:open) — never on the passive initial page-load
    // mount. Reconciling on load is what made a warm cart POST /cart/update.js
    // on every page view (the load-time cart write the perf audit flagged).
    document.addEventListener('cart:update', () =>
      queueMicrotask(() => {
        preserveDrawerOpenState();
        this.rebind();
        this.reconcile();
      })
    );
    // section:loaded fires on every section morph — re-wire the fresh DOM (and
    // restore the open-state flag the morph stripped), but do NOT reconcile here:
    // a morph is a render, not a new user intent, and the preceding cart:update
    // already reconciled.
    document.addEventListener('section:loaded', () =>
      queueMicrotask(() => {
        preserveDrawerOpenState();
        this.rebind();
      })
    );

    // First time the drawer is actually opened, reconcile once so gift/spoon
    // state is correct on the surface the user sees — without paying that cost
    // (or the /cart write) on every passive page load.
    host?.addEventListener('dialog:open', () => queueMicrotask(() => this.reconcile()));
  }

  /**
   * Wires the drawer on first mount.
   * @returns {void}
   */
  init() {
    this.rebind();
  }

  /**
   * (Re)wires every interactive behavior. Idempotent via the #wired WeakSet, so
   * it is safe to call on every cart:update / section:loaded.
   * @returns {void}
   */
  rebind() {
    this.wireUpsellArrows();
    this.wireUpsellCards();
    this.wireClimate();
    this.wireSubscriptionSelect();
    this.wireSpoonRemove();
    this.wireGiftRemove();
    this.wireLineRemove();
    this.wireQtyOneRemove();
    this.wireCheckoutLoading();
    this.wireQtyStepperLoading();
    this.wireImageSkeletons();
  }

  /**
   * Runs the gift/spoon/discount reconcilers. These can each POST to the cart
   * (/cart/change.js, /cart/add.js, /cart/update.js), so — unlike rebind() —
   * this MUST NOT run on the passive initial page-load mount: doing so made a
   * warm cart (subscription / GWP present) fire a load-time /cart/update.js on
   * every page view. It is only invoked from a real cart mutation (cart:update)
   * or when the drawer is opened (dialog:open). Each reconciler issues at most
   * one mutation per pass and self-converges, so repeated calls are safe.
   * @returns {void}
   */
  reconcile() {
    // Gift reconcilers: first-subscription measuring spoon, then gift-with-
    // purchase (honey dipper, etc.) and its discount code.
    this.evaluateSpoon();
    this.evaluateGift();
    this.applyAutoCodes();
    // Guardrail: the climate offset must never be the only line in the cart —
    // when the last real product leaves, remove it. One mutation per pass, then
    // the climate line count is 0 so it self-converges.
    this.evaluateClimateGuardrail();
  }

  /* ──── Upsell arrows ──────────────────────────────────────────────────── */

  /**
   * Wires the upsell rail's prev/next arrows and snaps the rail to its start.
   * @returns {void}
   */
  wireUpsellArrows() {
    const rail = this.root.querySelector('.awp_cart-upsell__rail');
    if (!(rail instanceof HTMLElement)) return;

    // First wiring per rail: snap to scrollLeft 0 so it always opens on the
    // first card, regardless of RTL inheritance or memoized scroll position.
    if (!this.#wired.has(rail)) {
      this.#wired.add(rail);
      rail.scrollLeft = 0;
    }

    const prev = this.root.querySelector('.awp_cart-upsell__arrow--prev');
    if (prev instanceof HTMLElement && !this.#wired.has(prev)) {
      this.#wired.add(prev);
      prev.addEventListener('click', () => rail.scrollBy({ left: -190, behavior: 'smooth' }));
    }

    const next = this.root.querySelector('.awp_cart-upsell__arrow--next');
    if (next instanceof HTMLElement && !this.#wired.has(next)) {
      this.#wired.add(next);
      next.addEventListener('click', () => rail.scrollBy({ left: 190, behavior: 'smooth' }));
    }
  }

  /* ──── Upsell card add-to-cart ────────────────────────────────────────── */

  /**
   * Wires each upsell card's "Add" button. Adds the (optionally grind-selected)
   * variant, matching the cart's default subscription plan when the cart is
   * subscription-only and the product offers that plan.
   * @returns {void}
   */
  wireUpsellCards() {
    this.root.querySelectorAll('.awp_cart-upsell-card').forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      const addBtn = card.querySelector('.awp_cart-upsell-card__add');
      if (!(addBtn instanceof HTMLElement)) return;
      if (this.#wired.has(addBtn)) return;
      this.#wired.add(addBtn);

      addBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        if (this.busy) return;

        const grindSelect = card.querySelector('.awp_cart-upsell-card__grind');
        const variantId =
          (grindSelect instanceof HTMLSelectElement ? grindSelect.value : '') || card.dataset.variantId;
        if (!variantId) return;

        /** @type {Record<string, any>} */
        const payload = { id: variantId, quantity: 1 };

        // Pre-order product: its only purchase option is a non-recurring
        // (deferred) selling plan. Always attach that plan so the item is added
        // AS a pre-order — never as a plain purchase or a subscription.
        const preorderPlanId = card.dataset.awpPreorderPlanId;
        if (preorderPlanId) {
          payload.selling_plan = preorderPlanId;
        } else {
          // Subscription-only cart → add as subscription matching the cart's
          // default plan, IF this product offers that plan. Mixed / one-time
          // carts always add the upsell as one-time.
          const cartMode = this.root.dataset.awpCartMode;
          const defaultPlanId = this.root.dataset.awpDefaultPlanId;
          if (cartMode === 'subscription-only' && defaultPlanId) {
            const availablePlanIds = (card.dataset.awpAvailablePlanIds || '').split(',').filter(Boolean);
            if (availablePlanIds.includes(defaultPlanId)) {
              payload.selling_plan = defaultPlanId;
            }
          }
        }

        await this.addToCart(payload, addBtn);
      });
    });
  }

  /* ──── Climate offset toggle ──────────────────────────────────────────── */

  /**
   * Wires the climate-offset row (add/remove on click) and its inline info
   * toggle. Reads the authoritative root dataset (re-rendered on each morph) so
   * a stale aria-pressed never double-adds.
   * @returns {void}
   */
  wireClimate() {
    const climate = this.root.querySelector('.awp_cart-climate');
    if (!(climate instanceof HTMLElement)) return;

    // Inline info-icon toggle (once per wrapper). stopPropagation so the row's
    // click handler doesn't also toggle the climate offset.
    const wrap = climate.closest('.awp_cart-climate-wrap');
    const toggle = wrap?.querySelector('.awp_cart-climate__toggle');
    const details = wrap?.querySelector('.awp_cart-climate__details');
    if (toggle instanceof HTMLElement && details instanceof HTMLElement && !this.#wired.has(toggle)) {
      this.#wired.add(toggle);
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (expanded) details.setAttribute('hidden', '');
        else details.removeAttribute('hidden');
      });
    }

    if (this.#wired.has(climate)) return;
    this.#wired.add(climate);

    // Row is a div with role=button — wire Enter/Space for keyboard parity.
    climate.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      if (e.target instanceof Element && e.target.closest('.awp_cart-climate__toggle')) return;
      e.preventDefault();
      climate.click();
    });

    climate.addEventListener('click', async (e) => {
      // Inline info button sits inside the row; ignore clicks coming from it.
      if (e.target instanceof Element && e.target.closest('.awp_cart-climate__toggle')) return;
      e.preventDefault();
      if (this.busy) return;

      const variantId = climate.dataset.variantId;
      const lineCount = parseInt(this.root.dataset.awpClimateLineCount || '0', 10);
      const existingKey = this.root.dataset.awpClimateFirstLineKey || '';

      if (lineCount > 0 && existingKey) {
        // Already in cart → click removes.
        this.setLoading(climate, true);
        await this.changeLine({ id: existingKey, quantity: 0 });
        this.setLoading(climate, false);
      } else if (lineCount === 0 && variantId) {
        this.setLoading(climate, true);
        await this.addToCart({ id: variantId, quantity: 1 }, climate);
        this.setLoading(climate, false);
      } else {
        console.warn('[awp-cart] climate click ignored', { variantId, lineCount, existingKey });
      }
    });
  }

  /* ──── Subscription cadence select ────────────────────────────────────────
     Empty string value = one-time purchase; any other value is a selling plan
     id. /change.js accepts selling_plan: '' to convert to one-time and
     selling_plan: <id> to subscribe (or change cadence). */

  /**
   * Wires the per-line cadence <select> and the one-time → subscribe button.
   * @returns {void}
   */
  wireSubscriptionSelect() {
    this.root.querySelectorAll('[data-awp-sub-select]').forEach((sel) => {
      if (!(sel instanceof HTMLSelectElement)) return;
      if (this.#wired.has(sel)) return;
      this.#wired.add(sel);

      sel.addEventListener('change', async () => {
        const item = sel.closest('.awp_cart-item');
        const lineKey = item instanceof HTMLElement ? item.dataset.key : '';
        if (!lineKey) return;
        // No-op guard: data-awp-current-plan is refreshed by the morph to the
        // line's ACTUAL plan. If the select already equals it, this "change"
        // came from the morph re-rendering the <select> (or a spurious WebKit
        // focus/blur change), not a user choice — skip it.
        const currentPlan = sel.dataset.awpCurrentPlan || '';
        if (sel.value === currentPlan) return;

        const chip = sel.closest('.awp_cart-sub-pick');
        this.setLoading(sel, true);
        chip?.classList.add('is-loading');
        try {
          await this.changeLine({ id: lineKey, selling_plan: sel.value });
        } finally {
          this.setLoading(sel, false);
          chip?.classList.remove('is-loading');
        }
      });
    });

    // Direct subscribe button (one-time → subscription at default cadence).
    this.root.querySelectorAll('[data-awp-sub-upgrade]').forEach((btn) => {
      if (!(btn instanceof HTMLElement)) return;
      if (this.#wired.has(btn)) return;
      this.#wired.add(btn);

      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const item = btn.closest('.awp_cart-item');
        const lineKey = item instanceof HTMLElement ? item.dataset.key : '';
        const planId = btn.dataset.planId;
        if (!lineKey || !planId) return;

        const chip = btn.closest('.awp_cart-sub-pick');
        this.setLoading(btn, true);
        chip?.classList.add('is-loading');
        try {
          await this.changeLine({ id: lineKey, selling_plan: planId });
        } finally {
          this.setLoading(btn, false);
          chip?.classList.remove('is-loading');
        }
      });
    });
  }

  /* ──── User remove buttons ────────────────────────────────────────────── */

  /* ──── Free measuring spoon (subscription gift) ───────────────────────────
     The only client-side auto-reconciler in this file. It is intentionally
     single-purpose and self-converging: it adds the spoon once whenever a
     subscription is in the cart, and removes it once when no subscription (or no
     real products) remains. The shopper can remove the spoon (see
     wireSpoonRemove); doing so records the current subscription line keys so the
     gift is NOT re-added until a new subscription line appears (a fresh
     qualifying action). After each pass the data-awp-spoon-* snapshot reflects
     the new state, so the matching condition is no longer true and it issues no
     further mutation — it cannot feed an update loop. (The GWP / climate /
     discount-code reconcilers were removed; only this gift is wanted client-side
     for now.) */

  /**
   * Reconciles the measuring-spoon gift against cart state. Reads the
   * data-awp-spoon-* / data-awp-cart-* snapshot the Liquid renders on every
   * morph. Issues at most one mutation per pass and converges.
   * @returns {Promise<void>}
   */
  async evaluateSpoon() {
    if (this.busy) return;

    const ds = this.root.dataset;
    const variantId = ds.awpSpoonVariantId;
    if (!variantId) return; // settings.awp_measuring_spoon_product not configured

    const inCart = ds.awpSpoonInCart === 'true';
    const lineKey = ds.awpSpoonLineKey || '';
    const customerEligible = ds.awpSpoonCustomerEligible === 'true';
    const hasSubscription = ds.awpCartHasSubscription === 'true';
    const cartMode = ds.awpCartMode || 'empty';
    // Comma-joined subscription line keys currently in the cart, and the set that
    // was present when the shopper last removed the spoon (a dismissal record).
    const subSignature = ds.awpSubSignature || '';
    const dismissedSignature = ds.awpSpoonDismissedSig || '';

    // Spoon present but the cart no longer warrants it (no subscription, or only
    // auxiliary lines) → remove it, and clear any dismissal record so the next
    // subscription re-offers the gift cleanly.
    if (inCart && lineKey && (cartMode === 'empty' || !hasSubscription)) {
      this.busy = true;
      try {
        const data = await cartFetch(CART_CHANGE_URL, { id: lineKey, quantity: 0 }, this.sectionId);
        if (isCartError(data)) throw new Error(data.description || data.message || 'Spoon remove failed');
        if (dismissedSignature) await this.updateAttribute({ awp_spoon_dismissed_sig: '' });
        dispatchCartUpdate(data, this.sectionId);
      } catch (err) {
        console.error('[awp-cart] spoon auto-remove failed', err);
      } finally {
        this.busy = false;
      }
      return;
    }

    // No subscription but a dismissal record lingers → clear it (attribute only).
    if (!hasSubscription && dismissedSignature) {
      await this.updateAttribute({ awp_spoon_dismissed_sig: '' });
      return;
    }

    // Eligible subscriber, spoon not present → add, UNLESS the shopper removed it
    // and no new subscription has been added since. The dismissal records the
    // subscription line keys present at removal; a current key absent from that set
    // means a new subscription was added (a fresh qualifying action) → re-offer.
    if (!inCart && hasSubscription && customerEligible) {
      if (dismissedSignature) {
        const dismissedKeys = dismissedSignature.split(',');
        const currentKeys = subSignature ? subSignature.split(',') : [];
        const hasNewSubscription = currentKeys.some((k) => !dismissedKeys.includes(k));
        if (!hasNewSubscription) return; // still dismissed; no new subscription yet
      }
      this.busy = true;
      try {
        const res = await fetch(CART_ADD_URL, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1, properties: { _free_gift: 'First-subscription measuring spoon' } }],
            sections: this.sectionId,
          }),
        });
        const data = await res.json();
        if (isCartError(data)) throw new Error(data.description || data.message || 'Spoon add failed');
        dispatchCartUpdate(data, this.sectionId);
      } catch (err) {
        console.error('[awp-cart] spoon auto-add failed', err);
      } finally {
        this.busy = false;
      }
    }
  }

  /**
   * Wires the spoon remove "X". Records the subscription line keys present at
   * removal in the awp_spoon_dismissed_sig cart attribute, so evaluateSpoon will
   * not re-add the gift until a new subscription line appears.
   * @returns {void}
   */
  wireSpoonRemove() {
    this.root.querySelectorAll('[data-awp-spoon-remove]').forEach((btn) => {
      if (!(btn instanceof HTMLElement)) return;
      if (this.#wired.has(btn)) return;
      this.#wired.add(btn);

      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        if (this.busy) return;
        const item = btn.closest('.awp_cart-item');
        const lineKey = item instanceof HTMLElement ? item.dataset.key : '';
        if (!lineKey) return;

        this.busy = true;
        item?.classList.add('is-loading');
        this.setLoading(btn, true);
        try {
          // Snapshot the subscriptions present now; the gift stays removed until a
          // line key outside this set appears (a newly added subscription). The
          // '-' fallback guards the unexpected no-signature case from re-adding.
          const subSignature = this.root.dataset.awpSubSignature || '-';
          await this.updateAttribute({ awp_spoon_dismissed_sig: subSignature });
          const data = await this.removeLineByKey(lineKey);
          if (data) dispatchCartUpdate(data, this.sectionId);
        } catch (err) {
          console.error('[awp-cart] spoon remove failed', err);
          showError(this.root, errorMessage(err));
          item?.classList.remove('is-loading');
          this.setLoading(btn, false);
        } finally {
          this.busy = false;
        }
      });
    });
  }

  /* ──── Gift-with-purchase (honey dipper, etc.) ────────────────────────────
     Reconciles GWP gift lines against the trigger products in the cart. A gift
     variant is "desired" when a product in the cart carries the
     custom.gift_with_purchase metafield pointing at it (Liquid computes this
     into data-awp-gwp-desired). Added gift lines carry the _gwp property and are
     listed in data-awp-gwp-present as "variantId|lineKey". One mutation per pass;
     the resulting morph re-runs this until the cart converges. */

  /**
   * Adds desired GWP gifts that aren't present (and weren't dismissed), and
   * removes gift lines whose trigger product has left the cart.
   * @returns {Promise<void>}
   */
  async evaluateGift() {
    if (this.busy) return;
    const ds = this.root.dataset;

    const desired = (ds.awpGwpDesired || '').split(',').map((s) => s.trim()).filter(Boolean);
    const present = (ds.awpGwpPresent || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((pair) => {
        // Pair is "variantId|lineKey". Split on the FIRST "|" only — the line key
        // itself is "{variant_id}:{hash}" and must stay intact.
        const idx = pair.indexOf('|');
        return { vid: pair.slice(0, idx), key: pair.slice(idx + 1) };
      })
      .filter((p) => p.vid && p.key);

    const desiredSet = new Set(desired);
    const presentSet = new Set(present.map((p) => p.vid));
    const dismissed = this.#gwpDismissedSet();

    // 1) Remove gift lines whose trigger product is no longer in the cart.
    const toRemove = present.filter((p) => !desiredSet.has(p.vid));
    if (toRemove.length) {
      this.busy = true;
      try {
        /** @type {any} */
        let data;
        for (const p of toRemove) {
          data = await cartFetch(CART_CHANGE_URL, { id: p.key, quantity: 0 }, this.sectionId);
          if (isCartError(data)) throw new Error(data.description || data.message || 'Gift remove failed');
        }
        if (data) {
          announce('Free gift removed from your cart.');
          dispatchCartUpdate(data, this.sectionId);
        }
      } catch (err) {
        console.error('[awp-cart] gift auto-remove failed', err);
      } finally {
        this.busy = false;
      }
      return;
    }

    // 2) Drop dismissed entries whose trigger is gone so re-adding the trigger
    //    re-offers the gift. Attribute-only change — no morph needed.
    let dismissChanged = false;
    for (const vid of [...dismissed]) {
      if (!desiredSet.has(vid)) {
        dismissed.delete(vid);
        dismissChanged = true;
      }
    }
    if (dismissChanged) {
      await this.updateAttribute({ awp_gwp_dismissed: [...dismissed].join(',') });
    }

    // 3) Add desired gifts that aren't present and weren't dismissed. No
    //    selling_plan — a gift must never recur as a subscription.
    const toAdd = desired.filter((vid) => !presentSet.has(vid) && !dismissed.has(vid));
    if (toAdd.length) {
      this.busy = true;
      try {
        /** @type {any} */
        let data;
        for (const vid of toAdd) {
          const res = await fetch(CART_ADD_URL, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              items: [{ id: vid, quantity: 1, properties: { _gwp: 'Gift with purchase' } }],
              sections: this.sectionId,
            }),
          });
          data = await res.json();
          if (isCartError(data)) throw new Error(data.description || data.message || 'Gift add failed');
        }
        if (data) {
          announce('Free gift added to your cart.');
          dispatchCartUpdate(data, this.sectionId);
        }
      } catch (err) {
        console.error('[awp-cart] gift auto-add failed', err);
      } finally {
        this.busy = false;
      }
    }
  }

  /**
   * Applies the cart's pending GWP discount codes (data-awp-auto-codes, built in
   * cart-drawer.liquid from each gift/trigger's custom.gift_with_purchase_code).
   * A code combines with the cart's one automatic discount, so it zeroes the gift
   * without consuming that slot. Skips codes already on the cart and codes already
   * attempted this session (#attemptedCodes only grows), so it converges.
   * @returns {Promise<void>}
   */
  async applyAutoCodes() {
    if (this.busy) return;
    const ds = this.root.dataset;

    const codes = (ds.awpAutoCodes || '').split(',').map((s) => s.trim()).filter(Boolean);
    if (!codes.length) return;

    const appliedCodes = new Set(
      (ds.awpAppliedCodes || '').split(',').map((s) => s.trim().toUpperCase()).filter(Boolean)
    );
    const toApply = codes.filter(
      (c) => !appliedCodes.has(c.toUpperCase()) && !this.#attemptedCodes.has(c.toUpperCase())
    );
    if (!toApply.length) return;

    this.busy = true;
    try {
      for (const code of toApply) {
        this.#attemptedCodes.add(code.toUpperCase());
        // /discount/{code} applies the code as a side effect (HTML redirect we
        // ignore — we only want the server-side effect).
        await fetch(`${DISCOUNT_BASE}${encodeURIComponent(code)}`, { method: 'GET', headers: { Accept: 'text/html' } });
      }
      // Re-render the section so the now-discounted gift price / totals show.
      const data = await cartFetch(CART_UPDATE_URL, {}, this.sectionId);
      if (!isCartError(data)) dispatchCartUpdate(data, this.sectionId);
    } catch (err) {
      console.error('[awp-cart] auto discount apply failed', err);
    } finally {
      this.busy = false;
    }
  }

  /* ──── Climate offset guardrail ───────────────────────────────────────────
     The climate offset is a user-toggled add-on, not a product in its own right
     — a cart holding ONLY the offset (no real coffee) is a nonsense checkout.
     When the last real product leaves, remove every climate line so the cart
     goes truly empty. One mutation per pass; afterwards the climate line count
     is 0, so the condition is false on the next pass — it cannot feed a loop. */

  /**
   * Removes the climate-offset line(s) when no real product remains in the cart,
   * so the offset can never be the only thing in the cart.
   * @returns {Promise<void>}
   */
  async evaluateClimateGuardrail() {
    if (this.busy) return;
    const ds = this.root.dataset;

    const lineCount = parseInt(ds.awpClimateLineCount || '0', 10);
    if (lineCount === 0) return;
    // data-awp-cart-mode is 'empty' exactly when no REAL product (excluding the
    // climate offset, spoon, and GWP gift) remains — see cart-drawer.liquid.
    if ((ds.awpCartMode || 'empty') !== 'empty') return;

    const keys = [ds.awpClimateFirstLineKey, ...(ds.awpClimateExtraLineKeys || '').split(',')]
      .map((s) => (s || '').trim())
      .filter(Boolean);
    if (!keys.length) return;

    this.busy = true;
    try {
      /** @type {any} */
      let data;
      for (const key of keys) {
        data = await cartFetch(CART_CHANGE_URL, { id: key, quantity: 0 }, this.sectionId);
        if (isCartError(data)) throw new Error(data.description || data.message || 'Climate auto-remove failed');
      }
      if (data) dispatchCartUpdate(data, this.sectionId);
    } catch (err) {
      console.error('[awp-cart] climate guardrail remove failed', err);
    } finally {
      this.busy = false;
    }
  }

  /**
   * Wires the gift-with-purchase remove "X". Records the gift's variant id in
   * the awp_gwp_dismissed cart attribute so evaluateGift won't immediately re-add
   * it, then re-homes focus after the morph.
   * @returns {void}
   */
  wireGiftRemove() {
    this.root.querySelectorAll('[data-awp-gwp-remove]').forEach((btn) => {
      if (!(btn instanceof HTMLElement)) return;
      if (this.#wired.has(btn)) return;
      this.#wired.add(btn);

      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        if (this.busy) return;
        const item = btn.closest('.awp_cart-item');
        if (!(item instanceof HTMLElement)) return;
        const lineKey = item.dataset.key;
        const variantId = item.dataset.awpGwpVariantId;
        if (!lineKey) return;

        this.busy = true;
        item.classList.add('is-loading');
        this.setLoading(btn, true);
        try {
          if (variantId) {
            const dismissed = this.#gwpDismissedSet();
            dismissed.add(String(variantId));
            await this.updateAttribute({ awp_gwp_dismissed: [...dismissed].join(',') });
          }
          const data = await this.removeLineByKey(lineKey);
          announce('Free gift removed from your cart.');
          this.#restoreFocusAfterMorph();
          if (data) dispatchCartUpdate(data, this.sectionId);
        } catch (err) {
          console.error('[awp-cart] gift remove failed', err);
          showError(this.root, errorMessage(err));
          item.classList.remove('is-loading');
          this.setLoading(btn, false);
        } finally {
          this.busy = false;
        }
      });
    });
  }

  /**
   * Wires the line-item remove "X" beside the product name (sets quantity 0).
   * @returns {void}
   */
  wireLineRemove() {
    this.root.querySelectorAll('[data-awp-line-remove]').forEach((btn) => {
      if (!(btn instanceof HTMLElement)) return;
      if (this.#wired.has(btn)) return;
      this.#wired.add(btn);

      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        if (this.busy) return;
        const item = btn.closest('.awp_cart-item');
        const lineKey = item instanceof HTMLElement ? item.dataset.key : '';
        if (!lineKey) return;

        this.busy = true;
        item?.classList.add('is-loading');
        this.setLoading(btn, true);
        try {
          const data = await this.removeLineByKey(lineKey);
          if (data) dispatchCartUpdate(data, this.sectionId);
        } catch (err) {
          console.error('[awp-cart] line remove failed', err);
          showError(this.root, errorMessage(err));
          item?.classList.remove('is-loading');
          this.setLoading(btn, false);
        } finally {
          this.busy = false;
        }
      });
    });
  }

  /* ──── Trash icon at qty=1 → remove the line ──────────────────────────────
     cart-quantity-selector-component sets minusButton.disabled = true whenever
     value <= min, which blocks click events. We use two pieces:
       1. A delegated click listener at the root that survives morphs.
       2. A MutationObserver that re-strips `disabled` whenever the component
          sets it on a .awp_cart-item--single .quantity-minus. */

  /**
   * Wires removal-at-qty-1: the single-item minus button acts as a trash icon.
   * @returns {void}
   */
  wireQtyOneRemove() {
    const undisable = () => {
      this.root.querySelectorAll('.awp_cart-item--single .quantity-minus').forEach((btn) => {
        if (!(btn instanceof HTMLButtonElement)) return;
        if (btn.hasAttribute('disabled')) btn.removeAttribute('disabled');
        if (btn.disabled) btn.disabled = false;
        btn.removeAttribute('on:click');
      });
    };
    undisable();

    if (this.#wired.has(this.root)) return;
    this.#wired.add(this.root);

    this.root.addEventListener(
      'click',
      async (e) => {
        if (!(e.target instanceof Element)) return;
        const btn = e.target.closest('.awp_cart-item--single .quantity-minus');
        if (!btn) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        if (this.busy) return;

        const item = btn.closest('.awp_cart-item');
        const lineKey = item instanceof HTMLElement ? item.dataset.key : '';
        if (!lineKey) return;

        this.busy = true;
        // Row-level loading only — the button itself is the trash icon, so a
        // spinner ::after on it would stack on the icon ::after.
        item?.classList.add('is-loading');
        try {
          const data = await this.removeLineByKey(lineKey);
          if (data) dispatchCartUpdate(data, this.sectionId);
        } catch (err) {
          console.error('[awp-cart] qty=1 remove failed', err);
          showError(this.root, errorMessage(err));
          item?.classList.remove('is-loading');
        } finally {
          this.busy = false;
        }
      },
      true
    );

    // Re-enable the minus button whenever cart-quantity-selector-component sets
    // disabled. Removing the attribute that triggered the observation breaks the
    // cycle after a single fire, so this can't loop.
    new MutationObserver(undisable).observe(this.root, {
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled'],
    });
  }

  /* ──── Quantity stepper +/- loading state ─────────────────────────────────
     The +/- buttons are wired by cart-quantity-selector-component itself. We
     can't intercept the request, but we can mark the row loading on click and
     release on the next cart:update. Skip the minus on .awp_cart-item--single
     since wireQtyOneRemove already handles its loading state. */

  /**
   * Marks the cart row loading when a qty stepper button is clicked.
   * @returns {void}
   */
  wireQtyStepperLoading() {
    const sentinel = this.root.querySelector('.awp_cart-body') || this.root;
    if (this.#wired.has(sentinel)) return;
    this.#wired.add(sentinel);

    this.root.addEventListener('click', (e) => {
      if (!(e.target instanceof Element)) return;
      const btn = e.target.closest('.quantity-plus, .quantity-minus');
      if (!btn) return;
      // Trash icon (qty=1 minus) is owned by wireQtyOneRemove.
      if (btn.classList.contains('quantity-minus') && btn.closest('.awp_cart-item--single')) return;
      const item = btn.closest('.awp_cart-item');
      if (!(item instanceof HTMLElement)) return;
      this.markLoadingUntilUpdate(item);
    });
  }

  /* ──── Checkout button loading state ──────────────────────────────────── */

  /**
   * Wires the checkout button so it shows a loading state while navigating.
   * @returns {void}
   */
  wireCheckoutLoading() {
    const checkout = this.root.querySelector('.awp_cart-checkout');
    if (!(checkout instanceof HTMLElement)) return;
    if (this.#wired.has(checkout)) return;
    this.#wired.add(checkout);

    checkout.addEventListener('click', (e) => {
      if (checkout.getAttribute('aria-disabled') === 'true') {
        e.preventDefault();
        return;
      }
      checkout.classList.add('is-loading');
      checkout.setAttribute('aria-busy', 'true');
      // Page navigates to /checkout — the loading state persists until the
      // browser swaps the document, so we don't clear it manually.
    });
  }

  /* ──── Line-item image skeleton ────────────────────────────────────────────
     A slow-loading product image leaves a confusing blank box. Show a skeleton
     shimmer until each image loads. The .is-loading class is toggled here (not
     in Liquid) so a cached/instant image never flashes a skeleton. */

  /**
   * Wires a skeleton shimmer on each line-item image until it loads.
   * @returns {void}
   */
  wireImageSkeletons() {
    this.root.querySelectorAll('.awp_cart-item__media img').forEach((img) => {
      if (!(img instanceof HTMLImageElement)) return;
      if (this.#wired.has(img)) return;
      this.#wired.add(img);

      const media = img.closest('.awp_cart-item__media');
      if (!(media instanceof HTMLElement)) return;
      const clear = () => media.classList.remove('is-loading');

      // Already decoded (cache hit) — no skeleton needed.
      if (img.complete && img.naturalWidth > 0) {
        clear();
        return;
      }
      media.classList.add('is-loading');
      img.addEventListener('load', clear, { once: true });
      img.addEventListener('error', clear, { once: true });
    });
  }

  /* ──── Cart mutations ─────────────────────────────────────────────────── */

  /**
   * Adds an item to cart and dispatches a morph. The `btn` is disabled during
   * the request; we only swap textContent for plain buttons (a single text
   * child, like the upsell "Add"). Rich buttons (icon + label + price, like the
   * climate row) are left intact so the morph re-renders them cleanly.
   * @param {Record<string, any>} payload - The add-to-cart line item payload.
   * @param {HTMLElement | null} btn - The triggering button to disable, if any.
   * @returns {Promise<void>}
   */
  async addToCart(payload, btn) {
    this.busy = true;
    const hasRichContent = btn != null && btn.children.length > 0;
    btn?.classList.add('is-loading');
    btn?.setAttribute('aria-busy', 'true');
    btn?.setAttribute('disabled', '');
    const originalText = btn && !hasRichContent ? btn.textContent : null;
    if (btn && !hasRichContent) btn.textContent = 'Adding…';
    try {
      const res = await fetch(CART_ADD_URL, {
        method: 'POST',
        headers,
        body: JSON.stringify({ items: [{ ...payload }], sections: this.sectionId }),
      });
      const data = await res.json();
      if (isCartError(data)) throw new Error(data.description || data.message || 'Add failed');
      dispatchCartUpdate(data, this.sectionId);
    } catch (err) {
      console.error('[awp-cart] add-to-cart failed', err);
      showError(this.root, errorMessage(err));
    } finally {
      this.busy = false;
      btn?.classList.remove('is-loading');
      btn?.removeAttribute('aria-busy');
      btn?.removeAttribute('disabled');
      if (btn && originalText !== null) btn.textContent = originalText;
    }
  }

  /**
   * Changes a single cart line (quantity or selling plan) and dispatches a morph.
   * @param {Record<string, any>} body - The /change.js body (id + change).
   * @returns {Promise<void>}
   */
  async changeLine(body) {
    this.busy = true;
    try {
      const data = await cartFetch(CART_CHANGE_URL, body, this.sectionId);
      if (isCartError(data)) throw new Error(data.description || data.message || 'Change failed');
      dispatchCartUpdate(data, this.sectionId);
    } catch (err) {
      console.error('[awp-cart] line change failed', err);
      showError(this.root, errorMessage(err));
    } finally {
      this.busy = false;
    }
  }

  /**
   * Pulls the current cart + section HTML WITHOUT mutating it, then morphs the
   * drawer. Used to recover when a remove targeted a line that was already gone:
   * the user's intent is satisfied, so we re-sync silently.
   * @returns {Promise<void>}
   */
  async resyncCart() {
    try {
      const res = await fetch(CART_UPDATE_URL, {
        method: 'POST',
        headers,
        body: JSON.stringify({ sections: this.sectionId }),
      });
      const data = await res.json();
      if (!isCartError(data)) dispatchCartUpdate(data, this.sectionId);
    } catch (err) {
      console.error('[awp-cart] cart re-sync failed', err);
    }
  }

  /**
   * Removes a cart line by key (quantity 0), tolerant of a stale key. Returns
   * the updated cart on success. If the line is already gone, re-syncs the
   * drawer and returns null so the caller skips its own dispatch. Throws only on
   * a genuine, unexpected failure.
   * @param {string} lineKey - The line key to remove.
   * @returns {Promise<any | null>} The updated cart, or null if already gone.
   */
  async removeLineByKey(lineKey) {
    const data = await cartFetch(CART_CHANGE_URL, { id: lineKey, quantity: 0 }, this.sectionId);
    if (isCartError(data)) {
      if (isStaleLineError(data)) {
        console.info('[awp-cart] remove hit a stale line key — re-syncing', lineKey);
        await this.resyncCart();
        return null;
      }
      throw new Error(data.description || data.message || 'Remove failed');
    }
    return data;
  }

  /**
   * Persists cart-level attributes (no morph). Failures are swallowed and logged
   * because attribute writes are best-effort bookkeeping.
   * @param {Record<string, string>} attributes - The attributes to set.
   * @returns {Promise<void>}
   */
  async updateAttribute(attributes) {
    try {
      await fetch(CART_UPDATE_URL, {
        method: 'POST',
        headers,
        body: JSON.stringify({ attributes }),
      });
    } catch (err) {
      console.error('[awp-cart] update attribute failed', err);
    }
  }

  /* ──── Loading-state helpers ──────────────────────────────────────────── */

  /**
   * Toggles a loading state on an interactive element (.is-loading + aria-busy,
   * disabling buttons/selects). CSS renders a spinner over the element.
   * @param {HTMLElement | null} el - The element to toggle.
   * @param {boolean} on - True to enable the loading state, false to clear it.
   * @returns {void}
   */
  setLoading(el, on) {
    if (!(el instanceof HTMLElement)) return;
    const disablable = el.tagName === 'BUTTON' || el.tagName === 'SELECT';
    if (on) {
      el.classList.add('is-loading');
      el.setAttribute('aria-busy', 'true');
      if (disablable) el.setAttribute('disabled', '');
    } else {
      el.classList.remove('is-loading');
      el.removeAttribute('aria-busy');
      if (disablable) el.removeAttribute('disabled');
    }
  }

  /**
   * Marks an element loading and clears it on the next cart:update OR after a
   * timeout (safety net if the morph fails). For fire-and-forget mutations like
   * qty +/- whose request is owned by another component.
   * @param {HTMLElement | null} el - The element to mark.
   * @param {number} [timeout=6000] - Fallback clear timeout in ms.
   * @returns {void}
   */
  markLoadingUntilUpdate(el, timeout = 6000) {
    if (!(el instanceof HTMLElement)) return;
    this.setLoading(el, true);
    let cleared = false;
    const clear = () => {
      if (cleared) return;
      cleared = true;
      this.setLoading(el, false);
      document.removeEventListener('cart:update', clear);
    };
    document.addEventListener('cart:update', clear, { once: true });
    setTimeout(clear, timeout);
  }

  /* ──── Private helpers ────────────────────────────────────────────────── */

  /**
   * Reads the dismissed-gift variant ids from the root dataset as a Set of
   * strings. These are gifts the customer explicitly removed.
   * @returns {Set<string>}
   */
  #gwpDismissedSet() {
    return new Set(
      (this.root.dataset.awpGwpDismissed || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    );
  }

  /**
   * After a user-initiated removal the row is destroyed by the section morph,
   * which would otherwise drop focus to <body> (WCAG 2.4.3). Re-home focus to
   * the drawer heading once the morph settles. The heading carries tabindex="-1".
   * @returns {void}
   */
  #restoreFocusAfterMorph() {
    const refocus = () => {
      document.removeEventListener('cart:update', refocus);
      // One frame past the morph so the freshly-rendered nodes exist.
      requestAnimationFrame(() => {
        const target =
          document.querySelector('.awp_cart-drawer__root #cart-drawer-heading') ||
          document.querySelector('.awp_cart-drawer__root .awp_cart-head__close');
        if (target instanceof HTMLElement) target.focus();
      });
    };
    document.addEventListener('cart:update', refocus, { once: true });
  }
}

/**
 * Bootstrap all drawer roots. The WeakSet tracks roots we've already wired so we
 * only ever create one controller per root, even after morph strips dataset
 * markers from the persisting DOM node.
 * @type {WeakSet<Element>}
 */
const wiredRoots = new WeakSet();

/**
 * Wires any not-yet-wired drawer roots in the document.
 * @returns {void}
 */
function init() {
  document.querySelectorAll('.awp_cart-drawer__root').forEach((root) => {
    if (!(root instanceof HTMLElement)) return;
    if (wiredRoots.has(root)) return;
    wiredRoots.add(root);
    new AwpCartDrawerController(root).init();
  });
}

/**
 * When the page loads with ?cart=open (set by the /cart → /collections redirect
 * in theme.liquid), open the cart drawer once its component is defined, then strip
 * the param so a reload or a shared/bookmarked URL doesn't re-trigger it.
 * @returns {void}
 */
function openDrawerFromUrl() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('cart') !== 'open') return;

  customElements.whenDefined('cart-drawer-component').then(() => {
    const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
    if (drawer && typeof drawer.open === 'function') drawer.open();

    params.delete('cart');
    const qs = params.toString();
    const url = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
    window.history.replaceState(window.history.state, '', url);
  });
}

// Build tag — lets us confirm at a glance which awp_cart-drawer.js the page is
// running. If you don't see this line in the console, the new code isn't live.
console.info('[awp-cart] drawer build: cart-drawer-v11 (reconcilers deferred off page load — no /cart write on passive load)');

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

openDrawerFromUrl();

// Re-init after section morphs (cart updates replace the drawer body).
document.addEventListener('cart:update', init);
document.addEventListener('section:loaded', init);
